import React, { useEffect, useState } from "react";
import StyledImage from "../../components/StyledComponents/StyledImage";
import StyledText from "../../components/StyledComponents/StyledText";
import UserIcon from "../../assets/svgIcons/UserIcon";
import EmailIcon from "../../assets/svgIcons/EmailIcon";
import PhoneIcon from "../../assets/svgIcons/PhoneIcon";
import ManageIdentities from "./ManageIdentities";
import StyledEditableText from "../../components/StyledComponents/StyledEditableText";
import {
  useIdentitiesListingQuery,
  useUpdateEmployeeMutation,
  useUpdateProfileMutation,
} from "../../services/apis";
import { useUser } from "../../hooks/useAuth";
import HomePageSkeleton from "./HomePageSkeleton";
import LogoutIcon from "../../assets/svgIcons/LogoutIcon";
import StyledToolTip from "../../components/StyledComponents/StyledToolTip";
import StyledButton from "../../components/StyledComponents/StyledButton";
import ResetPassword from "../../assets/svgIcons/ResetPassword";
import LoggedInResetPassword from "../LoggedInResetPassword";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { setUser } from "../../services/authSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

const Home = () => {
  const { user } = useUser();
  const dispatch = useDispatch();
  const [updateProfile] = useUpdateProfileMutation();
  const [getEmployeeInfo] = useUpdateEmployeeMutation();
  const [currentUser, setCurrentUser] = useState({});
  const [showResetPassword, setShowResetPassword] = useState(false);
  const navigate = useNavigate();

  const {
    data: identities,
    isLoading,
    refetch,
  } = useIdentitiesListingQuery(
    {
      userId: user?.id,
    },
    {
      skip: !user,
    },
  );

  useEffect(() => {
    if (user) {
      setCurrentUser({ ...user });
    }
  }, [user]);

  const handleInputChange = async (data) => {
    try {
      const { name: username, phone: number, email: mail } = currentUser

      if ([username, number, mail].includes(data?.value)) return

      let newUser = {
        ...currentUser,
        [data.objKey]: data.value,
      };
      const { name, phone, email } = newUser;

      const [firstName, ...lastName] = name?.split(" ");
     
      const call = user?.corporateId ? getEmployeeInfo : updateProfile;
      const { data: updatedUser, error } = await call({
        firstName,
        lastName: lastName?.join(" ") || "",
        phoneNumber: phone,
        email: email,
        id: user?.id
      });

      if (error) {
        console.error(error.data.detail || "Error updating profile");;
        toast.error(error.data.detail || "Error updating profile");;
        return;
      }

      newUser = {
        ...newUser,
        ...updatedUser,
      };
      setCurrentUser(newUser);
      dispatch(
        setUser({
          ...newUser,
          email: user?.email
        }),
      );
      toast.success(data.objKey === "email" ? "An email has been sent with verification link. Please check your mailbox" : `Profile ${data.objKey === "phone" ? "phone number" : "username"} updated successfully`);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("An unexpected error occurred. Please try again.");
    }
  };
  const getInitials = (name) => {
    if (!name || name === "-") {
      return "-";
    }

    return name
      ?.split(" ")?.slice(0, 2)
      ?.map((word) => word?.charAt(0))
      ?.join("")
      ?.toUpperCase();
  };

  if (isLoading) return <HomePageSkeleton />;

  return (
    <>
      <div className="p-md-3 __card-header d-flex align-items-center mb-5 mb-md-0">
        <StyledImage
          width="80px"
          height="80px"
          className="rounded-circle ms-md-3"
        >
          {user?.image ? (
            <img src={user.image} alt="user_image" />
          ) : (
            <div className="initials text-center">
              {getInitials(user?.name || "-")}
            </div>
          )}
        </StyledImage>
        <div className="d-none d-md-flex flex-column ps-4 gap-1">
          <StyledText $fontSize="0.9rem" color="white" $fontWeight="600">
            {user?.name || "-"}
          </StyledText>
          <StyledText $fontSize="0.8rem" color="white">
            {user?.email || "-"}
          </StyledText>
        </div>

        <StyledToolTip
          childrenClass="ms-auto cursor-pointer me-2 d-none d-md-flex"
          title="Logout"
        >
          <StyledText color="white" onClick={() => navigate("/logout")}>
            <LogoutIcon />
          </StyledText>
        </StyledToolTip>
      </div>
      <div className="__card-body">
        <div className="__card-body-inner p-4 p-md-4 m-0 m-lg-2 d-flex flex-column gap-2 gap-md-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <StyledText $fontSize="1rem" $fontWeight="600">
              Personal Information
            </StyledText>
            <StyledButton
              onClick={() => setShowResetPassword(true)}
              _variant="primaryGradiant"
              className="d-none d-md-flex align-items-center px-3"
              icon={<ResetPassword size="17px" />}
            >
              <StyledText $fontSize="14px" $fontWeight="600" color="white">
                Change Password
              </StyledText>
            </StyledButton>

            <StyledButton
              onClick={() => setShowResetPassword(true)}
              _variant="icon"
              className="d-flex d-md-none align-items-center"
              icon={<ResetPassword size={16} />}
            />
          </div>

          <Form className="__edit-personal-info">
            <div className="d-flex align-items-center mt-2 mb-3 my-md-4">
              <UserIcon />
              <StyledEditableText
                text={currentUser?.name || ""}
                objKey="name"
                onChange={handleInputChange}
              />
            </div>
            <div className="d-flex align-items-center mt-2 mb-3 my-md-4">
              <EmailIcon />
              <StyledEditableText
                text={currentUser?.email || ""}
                type="email"
                objKey="email"
                onChange={handleInputChange}
              />
            </div>

            <div className="d-flex align-items-center mt-2 mb-3 my-md-4">
              <PhoneIcon />
              <StyledEditableText
                text={currentUser?.phone || ""}
                type="phone"
                onChange={handleInputChange}
                objKey="phone"
              />
            </div>
          </Form>
        </div>
        <div className="__card-body-inner p-4 py-md-4 pt-md-0  m-0 m-lg-2">
          <ManageIdentities identities={identities?.content} refetch={refetch} />
        </div>
      </div>
      <LoggedInResetPassword
        show={showResetPassword}
        onHide={() => {
          setShowResetPassword(false);
        }}
      />
    </>
  );
};

export default Home;
